import { computed, readonly, ref } from 'vue';

export const DEFAULT_SCHOOL_ID = 'collegiate';
const SCHOOL_STORAGE_KEY = 'selectedSchoolId';
const TOKEN_STORAGE_KEY = 'token';

export const schoolConfigs = {
  collegiate: {
    id: 'collegiate',
    name: 'Collegiate School',
    menuSource: 'github',
    menuGithubRepo: 'jonahkleinman/bc-data',
    menuGithubBranch: 'main',
    menuSectionFilters: [/lower\s*school/i],
    menuSectionOrder: [
      /main\s*entree/i,
      /(hot\s*sandwich|action\s*station)/i,
      /pizza/i,
      /(soup|salad)/i,
      /(starch|vegetable|side)/i,
      /dessert/i,
    ],
    privacyContact: 'dpo@betterconnect.app',
  },
  grace: {
    id: 'grace',
    name: 'Grace Church School',
    menuSource: 'github',
    menuGithubRepo: 'jonahkleinman/bc-data',
    menuGithubBranch: 'grace',
    menuSectionFilters: [],
    menuSectionOrder: [],
    privacyContact: 'dpo@betterconnect.app',
  },
};

export const schoolOptions = Object.values(schoolConfigs);

function hasSchoolConfig(schoolId) {
  return Object.prototype.hasOwnProperty.call(schoolConfigs, schoolId);
}

export function normalizeSchoolId(schoolId) {
  const normalized = String(schoolId || '').trim().toLowerCase();
  return hasSchoolConfig(normalized) ? normalized : DEFAULT_SCHOOL_ID;
}

export function getStoredToken() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    return window.localStorage.getItem(TOKEN_STORAGE_KEY);
  } catch (error) {
    console.warn('Unable to read token from localStorage:', error);
    return null;
  }
}

export function getStoredSchoolId() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const schoolId = String(window.localStorage.getItem(SCHOOL_STORAGE_KEY) || '').trim().toLowerCase();
    return hasSchoolConfig(schoolId) ? schoolId : null;
  } catch (error) {
    console.warn('Unable to read school selection from localStorage:', error);
    return null;
  }
}

export function setStoredSchoolId(schoolId) {
  if (typeof window === 'undefined') {
    return null;
  }

  const normalizedSchoolId = String(schoolId || '').trim().toLowerCase();
  if (!hasSchoolConfig(normalizedSchoolId)) {
    return null;
  }

  try {
    window.localStorage.setItem(SCHOOL_STORAGE_KEY, normalizedSchoolId);
    return normalizedSchoolId;
  } catch (error) {
    console.warn('Unable to store school selection in localStorage:', error);
    return null;
  }
}

function decodeBase64UrlSegment(segment) {
  if (!segment) {
    return null;
  }

  try {
    const base64 = segment
      .replace(/-/g, '+')
      .replace(/_/g, '/')
      .padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=');

    const binary = atob(base64);
    const utf8 = Array.from(binary, (character) => `%${character.charCodeAt(0).toString(16).padStart(2, '0')}`).join('');
    return decodeURIComponent(utf8);
  } catch (error) {
    console.warn('Unable to decode JWT payload:', error);
    return null;
  }
}

export function getTokenPayload(token = getStoredToken()) {
  if (!token) {
    return null;
  }

  const [, payloadSegment] = token.split('.');
  if (!payloadSegment) {
    return null;
  }

  const decodedPayload = decodeBase64UrlSegment(payloadSegment);
  if (!decodedPayload) {
    return null;
  }

  try {
    return JSON.parse(decodedPayload);
  } catch (error) {
    console.warn('Unable to parse JWT payload JSON:', error);
    return null;
  }
}

export function getSchoolIdFromToken(token = getStoredToken()) {
  const schoolId = String(getTokenPayload(token)?.schoolId || '').trim().toLowerCase();
  return hasSchoolConfig(schoolId) ? schoolId : null;
}

export function getBuildTimeSchoolId() {
  const schoolId = String(import.meta.env.VITE_SCHOOL || '').trim().toLowerCase();
  return hasSchoolConfig(schoolId) ? schoolId : null;
}

export function resolveSchoolId({ schoolId = null, token = null, fallbackToDefault = true } = {}) {
  const explicitSchoolId = String(schoolId || '').trim().toLowerCase();
  if (hasSchoolConfig(explicitSchoolId)) {
    return explicitSchoolId;
  }

  const tokenSchoolId = getSchoolIdFromToken(token);
  if (tokenSchoolId) {
    return tokenSchoolId;
  }

  const storedSchoolId = getStoredSchoolId();
  if (storedSchoolId) {
    return storedSchoolId;
  }

  const buildTimeSchoolId = getBuildTimeSchoolId();
  if (buildTimeSchoolId) {
    return buildTimeSchoolId;
  }

  return fallbackToDefault ? DEFAULT_SCHOOL_ID : null;
}

export function getSchoolConfig(schoolId) {
  return schoolConfigs[resolveSchoolId({ schoolId })];
}

function normalizeText(value = '') {
  return String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
}

function matchesMenuRule(sectionName, rule) {
  if (!rule) {
    return false;
  }

  if (rule instanceof RegExp) {
    return rule.test(sectionName || '');
  }

  const normalizedName = normalizeText(sectionName);
  const normalizedRule = normalizeText(rule);
  return Boolean(normalizedRule) && normalizedName.includes(normalizedRule);
}

export function shouldHideMenuSection(sectionName, schoolId) {
  const school = getSchoolConfig(schoolId);
  return school.menuSectionFilters.some((rule) => matchesMenuRule(sectionName, rule));
}

export function getMenuSectionRank(sectionName, schoolId) {
  const school = getSchoolConfig(schoolId);
  const index = school.menuSectionOrder.findIndex((rule) => matchesMenuRule(sectionName, rule));
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

const activeSchoolId = ref(resolveSchoolId());
const activeSchool = computed(() => getSchoolConfig(activeSchoolId.value));

export function syncActiveSchool({ schoolId = null, token = null, persist = true } = {}) {
  const explicitSchoolId = String(schoolId || '').trim().toLowerCase();
  const normalizedExplicitSchoolId = hasSchoolConfig(explicitSchoolId) ? explicitSchoolId : null;
  const tokenSchoolId = getSchoolIdFromToken(token);

  if (persist) {
    if (normalizedExplicitSchoolId) {
      setStoredSchoolId(normalizedExplicitSchoolId);
    } else if (tokenSchoolId) {
      setStoredSchoolId(tokenSchoolId);
    }
  }

  activeSchoolId.value = resolveSchoolId({ schoolId: normalizedExplicitSchoolId, token });
  return activeSchoolId.value;
}

export function useSchoolContext() {
  return {
    activeSchoolId: readonly(activeSchoolId),
    activeSchool: readonly(activeSchool),
    syncActiveSchool,
  };
}
